import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { hasFirmPresence } from '../lib/firm-presence';
import { AuthShell, Alert, Button } from '@aigovops/design-system';
import { req } from '../lib/api';

/**
 * WorkspacePicker — /workspaces
 *
 * PR-P0-4 · ADR-0015 Post-signin routing case 5. Rendered when a
 * signed-in user has multiple memberships and no
 * `default_workspace_id`. Lists each workspace + "+ Create new
 * workspace" affordance.
 *
 * Delegates entry to `POST /api/w/:slug/session/enter` (same as
 * WorkspaceSwitcher). On MFA-required (401 with mfa_required), hard-
 * navigates to the WorkOS step-up URL. On success, navigates to the
 * workspace's dashboard.
 *
 * Also handles the shortcut cases so we don't strand a user here
 * unnecessarily:
 *   - 0 memberships → /onboarding/start (case 2)
 *   - 1 membership  → /w/{slug}/app/dashboard (case 3)
 *   - >1 memberships → render the picker (case 5)
 *
 * Case 4 (multi-workspace WITH default) is handled upstream in
 * AppRoot — the user never reaches /workspaces when they have a
 * default. Users can still hit /workspaces directly from any nav
 * to switch workspaces.
 */

type Row = {
  workspace_id: string;
  slug: string;
  name: string;
  kind: 'personal' | 'team' | 'consultant';
  role: string;
  last_seen_at?: string | null;
};

type ListResponse = { workspaces: Row[] };
type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: Row[] }
  | { kind: 'error'; message: string };

function sortRows(rows: Row[]): Row[] {
  return [...rows].sort((a, b) => {
    const at = a.last_seen_at ? Date.parse(a.last_seen_at) : NaN;
    const bt = b.last_seen_at ? Date.parse(b.last_seen_at) : NaN;
    const av = Number.isFinite(at);
    const bv = Number.isFinite(bt);
    if (av && bv) return bt - at;
    if (av) return -1;
    if (bv) return 1;
    return a.slug.localeCompare(b.slug);
  });
}

export function WorkspacePicker() {
  const navigate = useNavigate();
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [entering, setEntering] = useState<string | null>(null);
  const [enterError, setEnterError] = useState<string | null>(null);
  // PR-P0-4b · "Make this my default workspace" checkbox. When checked,
  // a PATCH to /api/auth/default-workspace runs before the picker
  // navigates so subsequent sign-ins land at the workspace's dashboard
  // instead of dropping through the picker again (ADR-0015 case 4).
  // Default TRUE — most users setting a workspace on the picker want
  // future sign-ins to remember. Consultants juggling clients uncheck.
  const [setDefault, setSetDefault] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const body = await req<ListResponse>('/api/workspaces');
        if (cancelled) return;
        const rows = sortRows(body.workspaces ?? []);
        if (rows.length === 0) {
          // T6-B1: an audit-firm member has no client workspace — their home is /audit-firm.
          navigate((await hasFirmPresence()) ? '/audit-firm' : '/onboarding/start', { replace: true });
          return;
        }
        if (rows.length === 1) {
          navigate(`/w/${rows[0].slug}/app/dashboard`, { replace: true });
          return;
        }
        setState({ kind: 'ready', rows });
      } catch (err) {
        if (!cancelled) {
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : String(err),
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  async function persistDefault(workspaceId: string) {
    // Best-effort: a failure here is a soft-degrade (user still gets
    // the picker again next sign-in) but should not block entering
    // the workspace they just picked. Log for observability.
    try {
      await req<unknown>('/api/auth/default-workspace', {
        method: 'PATCH',
        body: JSON.stringify({ workspace_id: workspaceId }),
      });
    } catch (err) {
      console.warn('[WorkspacePicker] set default_workspace failed:', err);
    }
  }

  async function enterAndNavigate(slug: string, workspaceId: string) {
    setEntering(slug);
    setEnterError(null);
    try {
      // Fire the default-workspace PATCH first (best-effort, doesn't
      // block navigation). Sequential rather than parallel because
      // a failure to persist default is silently degraded — no need
      // to race the enter request.
      if (setDefault) {
        await persistDefault(workspaceId);
      }
      const res = await fetch( // raw-fetch-ok: needs raw Response for MFA step_up_url
        `/api/w/${encodeURIComponent(slug)}/session/enter`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: '{}',
        }
      );
      if (res.ok) {
        navigate(`/w/${slug}/app/dashboard`);
        return;
      }
      if (res.status === 401) {
        let body: { error?: string; step_up_url?: string } = {};
        try {
          body = (await res.json()) as typeof body;
        } catch {
          /* ignore */
        }
        if (body.error === 'mfa_required' && body.step_up_url) {
          window.location.href = body.step_up_url;
          return;
        }
      }
      setEnterError(`Couldn't enter workspace (${res.status})`);
    } catch {
      setEnterError("Couldn't reach the server");
    } finally {
      setEntering(null);
    }
  }

  if (state.kind === 'loading') {
    return (
      <AuthShell title="Loading workspaces…" subtitle="Just a moment.">
        <div aria-busy="true" style={{ minHeight: 100 }} />
      </AuthShell>
    );
  }
  if (state.kind === 'error') {
    return (
      <AuthShell title="Couldn't load workspaces" subtitle="Try again in a moment.">
        <Alert variant="error">{state.message}</Alert>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Pick a workspace"
      subtitle="You're a member of multiple workspaces. Pick one to continue, or create a new one."
    >
      {enterError ? (
        <Alert variant="error">{enterError}</Alert>
      ) : null}
      <ul
        role="list"
        style={{
          listStyle: 'none',
          padding: 0,
          margin: 'var(--sp-3) 0 0',
          display: 'grid',
          gap: 'var(--sp-2)',
        }}
      >
        {state.rows.map((w) => {
          const isEntering = entering === w.slug;
          return (
            <li key={w.workspace_id}>
              <button
                type="button"
                onClick={() => enterAndNavigate(w.slug, w.workspace_id)}
                disabled={entering !== null}
                aria-label={`Enter ${w.name}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--sp-3)',
                  width: '100%',
                  padding: 'var(--sp-3) var(--sp-4)',
                  background: 'var(--surface-elevated)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-md)',
                  color: 'var(--text)',
                  fontSize: 'var(--fs-md)',
                  cursor: entering !== null ? 'wait' : 'pointer',
                  opacity: entering !== null && !isEntering ? 0.6 : 1,
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                <span
                  aria-hidden
                  style={{
                    width: 32,
                    height: 32,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--accent)',
                    color: 'var(--cta-text)',
                    fontSize: 'var(--fs-sm)',
                    fontWeight: 700,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {w.name.slice(0, 2).toUpperCase()}
                </span>
                <span style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
                  <span style={{ fontWeight: 500 }}>{w.name}</span>
                  <span
                    style={{
                      fontSize: 'var(--fs-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    {w.role}
                    {w.kind === 'personal' ? ' · Personal' : ''}
                    {isEntering ? ' · entering…' : ''}
                  </span>
                </span>
                <span aria-hidden style={{ color: 'var(--text-dim)', fontSize: 18 }}>
                  &rsaquo;
                </span>
              </button>
            </li>
          );
        })}
      </ul>
      <label
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginTop: 'var(--sp-4)',
          padding: 'var(--sp-3)',
          background: 'var(--surface-alt)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          fontSize: 'var(--fs-sm)',
          color: 'var(--text)',
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={setDefault}
          onChange={(e) => setSetDefault(e.target.checked)}
          disabled={entering !== null}
          style={{ margin: 0 }}
        />
        <span>Make this my default workspace. </span>
        <span style={{ color: 'var(--text-muted)' }}>
          Skip the picker on future sign-ins.
        </span>
      </label>
      <div style={{ marginTop: 'var(--sp-4)', textAlign: 'center' }}>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/onboarding/start')}
        >
          + Create a new workspace
        </Button>
      </div>
      <p
        style={{
          marginTop: 'var(--sp-4)',
          marginBottom: 0,
          fontSize: 'var(--fs-sm)',
          color: 'var(--text-muted)',
          textAlign: 'center',
        }}
      >
        <Link
          to="/"
          style={{
            color: 'var(--text-muted)',
            textDecoration: 'none',
          }}
          onClick={(e) => {
            e.preventDefault();
            const form = document.createElement('form');
            form.method = 'POST';
            form.action = '/api/auth/logout';
            document.body.appendChild(form);
            form.submit();
          }}
        >
          Sign out
        </Link>
      </p>
    </AuthShell>
  );
}
